import { FormInputSearch, FormSelect, FormSelectDataType } from '@systemeio/ui'
import { actionTitleLocoKeys } from 'modules/automation-rules/actions/constants/action-loco-keys'
import { defaultActionFilters } from 'modules/automation-rules/actions/constants/default-action-filters'
import {
  AutomationRuleActionsDataEnum,
  AutomationRuleActionsEnum,
} from 'modules/automation-rules/actions/enums/action-enum'
import { useActionsData } from 'modules/automation-rules/actions/hooks/use-actions-data'
import { AutomationRuleFilterInterface } from 'modules/automation-rules/types/automation-rule-filter-interface'
import { getData } from 'modules/automation-rules/utils/get-filter-data'
import { usePipelineStageList } from 'modules/crm/pipeline/hooks/use-pipeline-stage-list'
import { useEffect } from 'react'
import { FilterableItemType } from 'shared/components/listing-layout'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { getLocaleSelectOptions } from 'shared/utils/locale/get-locale-select-options'
import { useAllowedActions } from './use-allowed-actions'

export const useFilterableActionData = (responseFilter: AutomationRuleFilterInterface) => {
  const { t } = useLocoTranslation()

  const { pipelineStage, isLoading } = usePipelineStageList(responseFilter.pipelineId)

  const allowedActions = useAllowedActions()

  const actionData: FormSelectDataType<AutomationRuleActionsEnum>[] = allowedActions.map(slug => ({
    id: slug,
    caption: t(actionTitleLocoKeys[slug]),
  }))

  const { data, setShouldFetch } = useActionsData()

  useEffect(() => {
    setShouldFetch(prev => ({
      ...prev,
      [AutomationRuleActionsDataEnum.tag]: !!responseFilter.actionTagId,
      [AutomationRuleActionsDataEnum.campaign]: !!responseFilter.actionCampaignId,
      [AutomationRuleActionsDataEnum.mailing]: !!responseFilter.actionMailingId,
      [AutomationRuleActionsDataEnum.specific_mailing]: !!responseFilter.actionSpecificMailingId,
      [AutomationRuleActionsDataEnum.course]: !!responseFilter.actionCourseId,
      [AutomationRuleActionsDataEnum.locale]: !!responseFilter.locale,
      [AutomationRuleActionsDataEnum.webhook]: !!responseFilter.webhookUrl,
      [AutomationRuleActionsDataEnum.community]: !!responseFilter.actionCommunityId,
      [AutomationRuleActionsDataEnum.pipeline]: !!responseFilter.pipelineId,
      [AutomationRuleActionsDataEnum.course_bundle]: !!responseFilter.actionCourseBundleId,
      [AutomationRuleActionsDataEnum.sms_template]: !!responseFilter.actionSmsTemplateId,
    }))
  }, [responseFilter, setShouldFetch])

  const filterableAction: FilterableItemType<AutomationRuleFilterInterface>[] = [
    {
      field: 'action',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          data={actionData}
          key={'action-rule-action-filter'}
          placeholder={t('dashboard.automation_rule.choose_action_type')}
          value={value}
          onChange={data => onChangeState({ ...state, action: data, ...defaultActionFilters })}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.action === AutomationRuleActionsEnum.subscribe_to_campaign ||
        responseFilter.action === AutomationRuleActionsEnum.unsubscribe_from_campaign,
      field: 'actionCampaignId',
      onRender: (value, onChange) => (
        <FormSelect
          data={data.campaignData}
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleActionsDataEnum.campaign]: true,
            }))
          }
          key={'action-rule-campaign-filter'}
          placeholder={t('dashboard.automation_rule.choose_campaign')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.action === AutomationRuleActionsEnum.add_tag ||
        responseFilter.action === AutomationRuleActionsEnum.remove_tag,
      field: 'actionTagId',
      onRender: (value, onChange) => (
        <FormSelect
          data={data.tagData}
          onOpen={() =>
            setShouldFetch(prev => ({ ...prev, [AutomationRuleActionsDataEnum.tag]: true }))
          }
          key={'action-rule-tag-filter'}
          placeholder={t('dashboard.automation_rule.choose_tag')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.action === AutomationRuleActionsEnum.send_email,
      field: 'actionMailingId',
      onRender: (value, onChange) => (
        <FormSelect
          data={data.emailData}
          onOpen={() =>
            setShouldFetch(prev => ({ ...prev, [AutomationRuleActionsDataEnum.mailing]: true }))
          }
          key={'action-rule-email-filter'}
          placeholder={t('dashboard.automation_rule.choose_email')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.action === AutomationRuleActionsEnum.send_email_to_specific_address,
      field: 'actionSpecificMailingId',
      onRender: (value, onChange) => (
        <FormSelect
          data={data.specificEmailData}
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleActionsDataEnum.specific_mailing]: true,
            }))
          }
          key={'action-rule-email-specific-filter'}
          placeholder={t('dashboard.automation_rule.choose_email')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.action === AutomationRuleActionsEnum.enroll_in_course ||
        responseFilter.action === AutomationRuleActionsEnum.revoke_access_to_course,
      field: 'actionCourseId',
      onRender: (value, onChange) => (
        <FormSelect
          data={data.courseData}
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleActionsDataEnum.course]: true,
            }))
          }
          key={'action-rule-course-filter'}
          placeholder={t('dashboard.automation_rule.choose_course')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.action === AutomationRuleActionsEnum.enroll_in_course_bundle ||
        responseFilter.action === AutomationRuleActionsEnum.revoke_access_to_course_bundle,
      field: 'actionCourseBundleId',
      onRender: (value, onChange) => (
        <FormSelect
          data={data.courseBundleData}
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleActionsDataEnum.course_bundle]: true,
            }))
          }
          key={'action-rule-course-bundle-filter'}
          placeholder={t('dashboard.automation_rule.choose_course_bundle')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.action === AutomationRuleActionsEnum.create_user ||
        responseFilter.action === AutomationRuleActionsEnum.create_white_label_user,
      field: 'locale',
      onRender: (value, onChange) => (
        <FormSelect
          data={getLocaleSelectOptions(t)}
          key={'action-user-filter'}
          placeholder={t('dashboard.automation_rule.choose_language_of_user')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.action === AutomationRuleActionsEnum.send_webhook,
      field: 'webhookUrl',
      onRender: (value, onChange) => (
        <FormInputSearch
          onClear={() => onChange('')}
          key={'action-webhook-filter'}
          placeholder={t('dashboard.automation_rule.type_webhook')}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.action === AutomationRuleActionsEnum.grant_access_to_community ||
        responseFilter.action === AutomationRuleActionsEnum.revoke_access_to_community,
      field: 'actionCommunityId',
      onRender: (value, onChange) => (
        <FormSelect
          data={data.communitiesData}
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleActionsDataEnum.community]: true,
            }))
          }
          key={'action-community-filter'}
          placeholder={t(
            'dashboard.funnel.configuration.common.offer_resources.choose_community_access',
          )}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.action === AutomationRuleActionsEnum.assign_to_pipeline_stage,
      field: 'pipelineId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          data={data.pipelineData}
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleActionsDataEnum.pipeline]: true,
            }))
          }
          key={'action-pipeline-filter'}
          placeholder={t('dashboard.automation_rule.crm.select_pipeline')}
          value={value}
          onChange={data => {
            onChangeState({
              ...state,
              pipelineId: data,
              pipelineStageId: undefined,
            })
          }}
        />
      ),
    },
    {
      shouldRender: !!responseFilter.pipelineId,
      field: 'pipelineStageId',
      onRender: (value, onChange) => (
        <FormSelect
          key={'action-pipeline-stage-filter'}
          placeholder={t('dashboard.automation_rule.crm.select_pipeline_stage')}
          data={pipelineStage ? getData(Object.values(pipelineStage?.items)) : undefined}
          value={value}
          onChange={onChange}
          isPreFetching={isLoading}
        />
      ),
    },
    {
      shouldRender: responseFilter.action === AutomationRuleActionsEnum.send_sms,
      field: 'actionSmsTemplateId',
      onRender: (value, onChange) => (
        <FormSelect
          data={data.smsTemplateData}
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleActionsDataEnum.sms_template]: true,
            }))
          }
          key={'action-sms-template-filter'}
          placeholder={t('dashboard.automation_rule.choose_sms_template')}
          value={value}
          onChange={onChange}
        />
      ),
    },
  ]
  return filterableAction
}
